/**
 * Meeting Permission Adapter
 * Wraps MeetingService and direct API calls to provide unified permission interface
 */
import axios from 'axios';
import MeetingService from '../../MeetingService';
import { MEETING_ROLES } from '../constants';

const getTimestamp = (meetingUser) =>
  new Date(meetingUser.updatedAt || meetingUser.createdAt || 0).getTime();

const MeetingPermissionAdapter = {
  /**
   * Get all users with access to a meeting
   * @param {string} meetingId - Meeting ID
   * @returns {Promise<Array>} Array of meeting users with User data
   */
  async getUsers(meetingId) {
    const meetingUsers = await MeetingService.getMeetingUsers(meetingId);

    // Deduplicate by userId and keep the most recent role assignment
    const consolidatedUsersMap = new Map();
    meetingUsers.forEach((meetingUser) => {
      const existing = consolidatedUsersMap.get(meetingUser.userId);
      if (!existing) {
        consolidatedUsersMap.set(meetingUser.userId, meetingUser);
        return;
      }

      const existingTs = getTimestamp(existing);
      const incomingTs = getTimestamp(meetingUser);
      if (incomingTs >= existingTs) {
        consolidatedUsersMap.set(meetingUser.userId, meetingUser);
      }
    });

    const consolidatedUsers = Array.from(consolidatedUsersMap.values());

    // Transform to unified format
    return consolidatedUsers.map((meetingUser) => ({
      id: meetingUser.id,
      userId: meetingUser.userId,
      resourceId: meetingId,
      resourceType: 'meeting',
      role: meetingUser.userRole,
      user: meetingUser.User,
      // Keep original data for compatibility
      _original: meetingUser,
    }));
  },

  /**
   * Assign role to user(s) in a meeting
   * @param {string} meetingId - Meeting ID
   * @param {string|Array<string>} emails - Email(s) of user(s) to assign
   * @param {string} role - Role to assign
   * @param {Object} options - Additional options
   * @param {string} options.message - Optional message
   * @returns {Promise<Object>} Response data
   */
  async assignRole(meetingId, emails, role, options = {}) {
    const emailsList = Array.isArray(emails) ? emails : [emails];
    
    // Use existing meeting share endpoint
    const response = await axios.post(`/meetings/${meetingId}/share`, {
      emails: emailsList,
      userRole: role,
      message: options.message || '',
    });
    
    return response.data;
  },

  /**
   * Update user's role in a meeting
   * Note: Meeting roles are typically assigned via share endpoint
   * This is a placeholder for future implementation if needed
   * @returns {Promise<Object>} Updated meeting user data
   */
  async updateRole(meetingId, userId, role) {
    const meetingUsers = await MeetingService.getMeetingUsers(meetingId);
    const target = meetingUsers.find((meetingUser) => meetingUser.userId === userId);

    if (!target) {
      throw new Error('User does not have access to this video');
    }

    if (!target.User?.email) {
      throw new Error('Unable to update role: missing user email');
    }

    if (target.userRole === MEETING_ROLES.CREATOR) {
      throw new Error('You cannot change the role of the video creator');
    }

    const response = await axios.post(`/meetings/${meetingId}/share`, {
      emails: [target.User.email],
      userRole: role,
      message: '',
    });

    return response.data;
  },

  /**
   * Remove user access from a meeting
   * Note: This may not be directly supported by current API
   * @returns {Promise<Object>} Response data
   */
  async removeUser(meetingId, userId) {
    await axios.delete(`/meetings/${meetingId}/users/${userId}`);
    return true;
  },
};

export default MeetingPermissionAdapter;

